// src/pages/academy/CreateAcademyExam.jsx
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ClipboardList, Save, ArrowRight, Loader2 } from "lucide-react";
import { academyExamsAPI, getApiError } from "../../services/academyService";
import { ACADEMY_ROOT } from "./academyConstants";
import {
  IconField,
  ActiveToggle,
  Field,
  inputClass,
} from "./components/AcademyFormFields";

export default function CreateAcademyExam() {
  const navigate = useNavigate();
  // create: /categories/:categoryId/exams/create
  // edit:   /categories/:categoryId/exams/:examId/edit
  const { categoryId, examId } = useParams();
  const isEdit = Boolean(examId);
  const backPath = `${ACADEMY_ROOT}/categories/${categoryId}/exams`;

  const [form, setForm] = useState({
    title: "",
    description: "",
    order: 0,
    is_active: true,
  });
  const [iconFile, setIconFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [serverIcon, setServerIcon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit) fetchExam();
  }, [examId]);

  useEffect(() => {
    return () => {
      if (iconPreview) URL.revokeObjectURL(iconPreview);
    };
  }, [iconPreview]);

  const fetchExam = async () => {
    try {
      const data = await academyExamsAPI.getById(examId);
      setForm({
        title: data.title || "",
        description: data.description || "",
        order: data.order || 0,
        is_active: data.is_active ?? true,
      });
      setServerIcon(data.icon_url || null);
    } catch (err) {
      console.error(err);
      setError(getApiError(err, "تعذر تحميل بيانات الاختبار"));
    } finally {
      setFetching(false);
    }
  };

  const pickIcon = (file) => {
    setIconFile(file);
    setIconPreview(URL.createObjectURL(file));
  };

  const clearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      setError("عنوان الاختبار مطلوب");
      return;
    }
    try {
      setLoading(true);
      setError("");

      const formData = new FormData();
      formData.append("title", form.title.trim());
      formData.append("description", form.description || "");
      formData.append("order", form.order);
      formData.append("is_active", form.is_active ? "true" : "false");
      if (iconFile) formData.append("icon", iconFile);

      if (isEdit) {
        await academyExamsAPI.update(examId, formData);
      } else {
        await academyExamsAPI.create(categoryId, formData);
      }
      navigate(backPath);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(backPath)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowRight className="w-5 h-5 text-gray-600" />
        </button>
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <ClipboardList className="w-5 h-5 text-indigo-700" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">
          {isEdit ? "تعديل الاختبار" : "إضافة اختبار جديد"}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl border border-gray-200 p-6 space-y-5"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            {error}
          </div>
        )}

        <IconField
          serverIcon={serverIcon}
          newPreview={iconPreview}
          onPick={pickIcon}
          onClear={clearIcon}
        />

        <Field label="عنوان الاختبار" required>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="مثال: Midterm Exam"
            className={inputClass}
          />
        </Field>

        <Field label="الوصف">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="وصف مختصر للاختبار..."
            rows={3}
            className={`${inputClass} resize-none`}
          />
        </Field>

        <Field label="الترتيب">
          <input
            type="number"
            min={0}
            value={form.order}
            onChange={(e) =>
              setForm({ ...form, order: parseInt(e.target.value) || 0 })
            }
            className={inputClass}
          />
        </Field>

        <ActiveToggle
          value={form.is_active}
          onChange={(v) => setForm({ ...form, is_active: v })}
        />

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(backPath)}
            className="flex-1 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 font-medium"
          >
            إلغاء
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            {isEdit ? "حفظ التعديلات" : "إنشاء الاختبار"}
          </button>
        </div>
      </form>
    </div>
  );
}